<template>
    <div>
        <div v-if="data.length">
            <b-media class="mb-4" v-for="actionPlan in data" :key="actionPlan.id">
                <b-media right-align vertical-align="right">
                    <b-media right-align vertical-align="center">
                        <div slot="aside">
                            <b-button
                                id="btn-tab-home-pdi-checkin"
                                type="button"
                                variant="link"
                                tooltip="teste"
                                :title="$t('pdis.checkin')"
                                @click="openTheModalCheckIn(actionPlan.id)"
                            >
                                <i class="fa fa-calendar-check-o fa-lg" />
                                <p class="m-0">
                                    <small>Check-in</small>
                                </p>
                            </b-button>

                            <b-dropdown
                                size="sm"
                                variant="link"
                                :no-caret="true"
                                :dropleft="true"
                                :title="$t('pdis.updateOrDelete')"
                            >
                                <template slot="button-content">
                                    <i class="fa fa-ellipsis-v fa-lg" />
                                </template>
                                <b-dropdown-item
                                    id="btn-tab-home-pdi-action-plan-edit"
                                    @click="openEditActionPlanModal(actionPlan.id)"
                                    >{{ $t('form.button.edit') }}</b-dropdown-item
                                >
                                <b-dropdown-item
                                    id="btn-tab-home-pdi-action-plan-delete"
                                    @click="deleteActionPlan(actionPlan.id)"
                                    >{{ $t('form.button.delete') }}</b-dropdown-item
                                >
                            </b-dropdown>
                        </div>

                        <b-container class="bv-example-row bv-example-row-flex-cols">
                            <b-row>
                                <b-col align-self="start" cols="6">
                                    <div class="d-flex justify-content-between">
                                        <div class="mr-2">
                                            <p class="mb-0 text-justify text-small-sm-down">
                                                <b-link
                                                    @click="goToActionPlanView(actionPlan.id)"
                                                    class="font-weight-bold"
                                                >
                                                    {{ actionPlan.title }}
                                                </b-link>
                                            </p>
                                        </div>
                                    </div>
                                </b-col>
                                <b-col align-self="center" cols="2">
                                    <b-button size="sm" class="ml-1 cursor" type="button" variant="outline">
                                        <i class="fa fa-calendar fa-lg"></i>
                                        {{ getFormatDateForBrazilianFormat(actionPlan.due_date) }}
                                    </b-button>
                                </b-col>
                                <b-col>
                                    <b-row class="justify-content-md-end">
                                        <b-col md="10" sm="12" lg="9">
                                            <i class="fa fa-chart-bar fa-lg"></i>
                                            {{ $t(`pdis.checkins.form.labels.${actionPlan.status}`) }}
                                        </b-col>
                                        <b-col md="1" sm="12" lg="3">
                                            <span class="text-muted mb-1">
                                                {{
                                                    actionPlan.current_value !== null
                                                        ? `${formatPercentage(actionPlan.current_value)}%`
                                                        : '-'
                                                }}
                                            </span>
                                        </b-col>
                                        <b-col md="1" sm="12" lg="12">
                                            <progress-bar
                                                :value="actionPlan.current_value"
                                                :color="calculateColor(actionPlan.current_value)"
                                            />
                                        </b-col>
                                    </b-row>
                                </b-col>
                            </b-row>
                        </b-container>
                    </b-media>
                    <edit-action-plan-modal :actionPlanId="actionPlan.id" @reloadPdis="$emit('actionPlanUpdated')" />
                </b-media>
            </b-media>
        </div>
        <b-alert show variant="info" v-else>{{ $t('profile.objectivesTable.empty') }}</b-alert>
    </div>
</template>

<script>
import Avatar from 'vue-avatar'
import { formatPercentage } from '@/lib/utils'
import Swal from 'sweetalert2'
import i18n from '@/i18n'
import { deleteConfirmationWithLoading } from '@/config/sweet-alert'
import notification, { success, danger } from '@/lib/notification'
import api from '@/services/api'
import { getDateToBrazilianDefault } from '@/lib/date'
import ProgressBar from '@/views/components/progress-bar/ProgressBar'
import { EditActionPlan as EditActionPlanModal } from '@/views/pdi'

notification.init()

export default {
    name: 'ActionPlans',
    data() {
        return {
            status: '',
        }
    },
    props: ['data', 'userId', 'currentValue', 'pdiId'],
    components: {
        Avatar,
        ProgressBar,
        EditActionPlanModal,
    },
    methods: {
        calculateColor(currentValue) {
            if (currentValue === 0) {
                this.status = i18n.t('pdis.notStarted')
                return 'red'
            }

            if (currentValue > 0 && currentValue < 100) {
                this.status = i18n.t('pdis.inProgress')
                return 'yellow'
            }

            if (currentValue >= 100) {
                this.status = i18n.t('pdis.completed')
                return 'green'
            }
        },
        openTheModalCheckIn(actionPlanId) {
            this.$router.push({
                path: `/v2/profile/${this.userId}/pdi/${this.pdiId}/action-plan/${actionPlanId}/checkin`,
            })
        },
        getFormatDateForBrazilianFormat(date) {
            return getDateToBrazilianDefault(date, 'DD/MM/YYYY')
        },
        formatPercentage(number) {
            return formatPercentage(number)
        },
        openEditActionPlanModal(id) {
            this.$bvModal.show(`edit-action-plan-modal${id}`)
        },
        goToActionPlanView(id) {
            return this.$router.push(`/v2/action-plan/view/${id}`)
        },
        deleteActionPlan(id) {
            Swal({
                ...deleteConfirmationWithLoading,
                preConfirm: () => {
                    return this.deleteActionPlanFromApi(id)
                },
            })
                .then((resp) => {
                    const { value } = resp
                    if (value) {
                        this.notificationActionPlanDeletedSuccessfully()
                        this.$emit('actionPlanUpdated')
                    }
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        deleteActionPlanFromApi(id) {
            return api.delete(`/action-plans/${id}`).catch((e) => {
                throw new e()
            })
        },
        notificationActionPlanDeletedSuccessfully() {
            success(
                i18n.t('notification.deletedSuccessfully', {
                    item: i18n.t('form.label.objective'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
}
</script>

<style scoped>
@media (max-width: 575px) {
    .text-small-sm-down {
        font-size: 12px !important;
    }
    .hidden-sm-down {
        display: none !important;
    }
}

.cursor {
    cursor: unset;
}
</style>
